import { connect } from "react-redux";
import { Spinner } from "@fluentui/react-components";
import { FontIcon } from "@fluentui/react";
import { useEffect, useRef } from "react";

import SettlementDebtsInfoComponent from "./SettlementDebtsInfo.Component";
import * as ComponentFunctions from './SettlementDebtsDetails.Functions';
import SettlementChangeStatusConfirmationFormComponent from "../SettlementChangeStatusConfirmationForm/SettlementChangeStatusConfirmation.Form.Component";
import { SettlementStatusEnum } from "../../../models/Enums/SettlementStatusEnum";
import './SettlementDebtsDetails.Styles.css';

const SettlementDebtsDetailsComponent = (props: ComponentFunctions.Props) => {
    const pollRef = useRef<number | null>(null);

    useEffect(() => {
        props.ResetSettlementChangeStatusFlags(props.SettlementId);
        props.GetSettlementDebtsBySettlementId(props.SettlementId);
    }, [props.SettlementId]);

    useEffect(() => {
        const hasDebts = (props.Debts?.length ?? 0) > 0;
        const hasEnrichedDebts = props.Debts?.some(d => !!d.CashFlowDebt) ?? false;
        const isConfirmed = props.settlement?.status === SettlementStatusEnum.CONFIRMED;

        if (hasDebts && !hasEnrichedDebts) {
            props.GetDebtsBySettlementId(props.SettlementId, undefined, false);
        }

        if (isConfirmed && !hasDebts && pollRef.current === null) {
            pollRef.current = window.setInterval(() => {
                props.GetSettlementDebtsBySettlementId(props.SettlementId);
            }, 5000);
        }

        if ((hasDebts || !isConfirmed) && pollRef.current !== null) {
            window.clearInterval(pollRef.current);
            pollRef.current = null;
        }

        return () => {
            if (pollRef.current !== null) {
                window.clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [props.settlement?.status, props.Debts?.length, props.Debts?.some(d => !!d.CashFlowDebt), props.SettlementId]);

    useEffect(() => {
        if (props.isChangingSettlementStatusSuccessfully) {
            props.GetSettlementById(props.SettlementId);
            props.GetSettlementDebtsBySettlementId(props.SettlementId);
            props.ResetSettlementChangeStatusFlags(props.SettlementId);
        }
    }, [props.isChangingSettlementStatusSuccessfully]);

    const isBusy = props.isLoading || props.isChangingSettlementStatus === true;
    const debts = props.Debts ?? [];
    const hasDebts = debts.length > 0;
    const showWarning = props.settlement?.status === SettlementStatusEnum.CONFIRMED && !hasDebts;

    // Computed stats from enriched debts
    const enriched = debts.filter(d => !!d.CashFlowDebt);
    const paidCount = enriched.filter(d => d.CashFlowDebt!.pendingAmount === 0).length;
    const pendingCount = enriched.filter(d => d.CashFlowDebt!.pendingAmount > 0).length;
    const hasPending = pendingCount > 0;

    const rootMod = showWarning ? ' sdd-root--warning'
        : hasPending       ? ' sdd-root--pending'
        : hasDebts         ? ' sdd-root--resolved'
        :                    ' sdd-root--info';

    return (
        <div className={`sdd-root${rootMod}`}>

            {/* ── Header ─────────────────────────────────────────────── */}
            <div className="sdd-header">
                <div className="sdd-title-row">
                    <FontIcon iconName="PaymentCard" className="sdd-title-icon" />
                    <span className="sdd-title">Deudas asociadas</span>
                    {hasDebts && (
                        <span className={`sdd-count-badge${hasPending ? ' sdd-count-badge--pending' : ' sdd-count-badge--resolved'}`}>
                            {debts.length}
                        </span>
                    )}
                </div>
                <div className="sdd-actions">
                    {isBusy && (
                        <Spinner size="tiny" label="Verificando..." labelPosition="before" />
                    )}
                    <SettlementChangeStatusConfirmationFormComponent
                        SettlementId={props.SettlementId}
                        NextStatusIsActionConfirmed={SettlementStatusEnum.PAID}
                        disabled={isBusy}
                    />
                </div>
            </div>

            {/* ── Stats strip (only when debts are enriched) ──────────────── */}
            {hasDebts && enriched.length > 0 && (
                <div className="sdd-stats">
                    <div className="sdd-stat">
                        <span className="sdd-stat-label">Total</span>
                        <span className="sdd-stat-value">{debts.length}</span>
                    </div>
                    <div className="sdd-stat sdd-stat--paid">
                        <FontIcon iconName="CheckMark" className="sdd-stat-icon" />
                        <span className="sdd-stat-label">Pagadas</span>
                        <span className="sdd-stat-value sdd-stat-value--paid">{paidCount}</span>
                    </div>
                    <div className={`sdd-stat${pendingCount > 0 ? ' sdd-stat--pending' : ''}`}>
                        <FontIcon iconName={pendingCount > 0 ? 'Warning' : 'CheckMark'} className="sdd-stat-icon" />
                        <span className="sdd-stat-label">Pendientes</span>
                        <span className={`sdd-stat-value${pendingCount > 0 ? ' sdd-stat-value--pending' : ''}`}>
                            {pendingCount}
                        </span>
                    </div>
                </div>
            )}

            {/* ── Debt list or warning ──────────────────────────────── */}
            {hasDebts ? (
                <SettlementDebtsInfoComponent SettlementDebts={debts} />
            ) : (
                <div className="sdd-empty">
                    <FontIcon iconName="Clock" className="sdd-empty-icon" />
                    <div className="sdd-empty-text">
                        <strong>Liquidación confirmada</strong> — las deudas aún no fueron generadas.
                        <br />
                        <span className="sdd-empty-hint">Se verificará automáticamente cada 5 segundos.</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(SettlementDebtsDetailsComponent as any);